import Page404 from "404";
import Loader from "@/components/ui/Loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import useSWR from "swr";
import { ArrowLeft } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { DraftPublishButton } from "./DraftPublishButton";
// import { DraftProductIdsEditor } from "./DraftProductIdsEditor";
import { RelatedTemplatesOnlineCheck } from "./RelatedTemplatesOnlineCheck";
import { TemplateInitDataInfoDisplay } from "AdminPanel/Spine/editor/panel/info-panel/components/TemplateInitDataInfoDisplay";

function mediaUrlRow(label: string, url?: string) {
  if (!url?.trim()) return null;
  return (
    <code className="block break-all text-xs text-muted-foreground">
      {label}: {url}
    </code>
  );
}

function detailRow(
  label: string,
  value: string | number | boolean | null | undefined,
): React.ReactNode {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return (
    <div className="grid grid-cols-[10rem,1fr] gap-2 border-b border-border/60 py-2 text-sm last:border-0 sm:grid-cols-[12rem,1fr]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium">{String(value)}</dd>
    </div>
  );
}

const DraftReview = () => {
  const { tid } = useParams<{ tid: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<any>(
    tid ? [`/spine-drafts/${tid}`] : null,
  );

  if (isLoading) {
    return <Loader>Loading draft…</Loader>;
  }
  if (!data) {
    return <Page404 />;
  }

  return (
    <div className="flex-1 w-full space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void navigate("/admin/spine?spineTab=draft")}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-3xl font-bold tracking-tight flex-1 min-w-0 break-all">
          {data.id || tid}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          <DraftPublishButton draftId={data.id} />
        </div>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">Checks</h2>
        <RelatedTemplatesOnlineCheck draft={data} variant="spineDraft" />
      </section>

      {/* 暂不开放审核页配置上线产品，默认全产品上线
      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          上线产品
        </h2>
        <DraftProductIdsEditor
          draftId={data.id}
          productIds={data.productIds}
        />
      </section>
      */}

      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">Summary</h2>
        <dl className="rounded-md border border-border px-2">
          {detailRow("Name", data.name)}
          {detailRow("Type", data.type)}
          {detailRow("Short ID", data.shortId)}
          {data.intro != null && data.intro !== "" ? (
            <div className="border-b border-border/60 py-2 last:border-0">
              <dt className="text-muted-foreground mb-1 text-sm">Intro</dt>
              <dd className="whitespace-pre-wrap text-sm font-medium">{data.intro}</dd>
            </div>
          ) : null}
          {detailRow("initDataId", data.initDataId)}
          {detailRow("spineVersion", data.spineVersion)}
          {detailRow("Author", data.author)}
          {detailRow("Publisher", data.publisher)}
          {detailRow("Character", data.character)}
          {data.cover ||
          data.videoCover ||
          data.videoCoverPreview ||
          data.preview ||
          data.videoPreview ||
          data.videoPreviewPreview ? (
            <div className="border-b border-border/60 py-3 last:border-0">
              <TemplateInitDataInfoDisplay
                cover={data.cover}
                videoCover={data.videoCover}
                videoCoverPreview={data.videoCoverPreview}
                preview={data.preview}
                videoPreview={data.videoPreview}
                videoPreviewPreview={data.videoPreviewPreview}
                tileClassName="h-40 w-40 shrink-0"
              />
              <div className="mt-3 space-y-1">
                {mediaUrlRow("cover", data.cover)}
                {mediaUrlRow("videoCover", data.videoCover)}
                {mediaUrlRow("videoCoverPreview", data.videoCoverPreview)}
                {mediaUrlRow("preview", data.preview)}
                {mediaUrlRow("videoPreview", data.videoPreview)}
                {mediaUrlRow("videoPreviewPreview", data.videoPreviewPreview)}
              </div>
            </div>
          ) : null}
          {detailRow("spineZipUrl", data.spineZipUrl)}
          {detailRow("spineCompressedZipUrl", data.spineCompressedZipUrl)}
          {detailRow("spineFingerprint", data.spineFingerprint)}
          {detailRow("Animations", data.animations)}
          {data.relatedTemplateIds?.length
            ? detailRow("relatedTemplateIds", JSON.stringify(data.relatedTemplateIds))
            : null}
          {data.createTime != null && (
            <div className="grid grid-cols-[10rem,1fr] gap-2 border-b border-border/60 py-2 text-sm sm:grid-cols-[12rem,1fr]">
              <dt className="text-muted-foreground">Create time</dt>
              <dd>
                <DisplayUpdateTime updateTime={data.createTime} />
              </dd>
            </div>
          )}
          {data.updateTime != null && (
            <div className="grid grid-cols-[10rem,1fr] gap-2 py-2 text-sm sm:grid-cols-[12rem,1fr]">
              <dt className="text-muted-foreground">Update time</dt>
              <dd>
                <DisplayUpdateTime updateTime={data.updateTime} />
              </dd>
            </div>
          )}
        </dl>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">Full document</h2>
        <Dialog>
          <DialogTrigger asChild>
            <Button type="button" variant="outline">
              View full JSON
            </Button>
          </DialogTrigger>
          <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
            <DialogHeader>
              <DialogTitle>Spine draft</DialogTitle>
            </DialogHeader>
            <pre className="min-h-0 flex-1 overflow-auto rounded-md bg-muted p-3 text-left text-xs leading-relaxed">
              {JSON.stringify(data, null, 2)}
            </pre>
          </DialogContent>
        </Dialog>
      </section>
    </div>
  );
};

export default DraftReview;
